import React, { useState, useEffect, useRef, useCallback } from "react";
import privateAxios from "../../utils/axiosPrivate";
import PropTypes from "prop-types";
import { FaCheck } from "react-icons/fa";
import PhoneInput from "react-phone-input-2";
import {
  showToast,
  isValidPhoneLength,
  stripTrunkZeroAfterCountryCode
} from "../../utils/utils";
import "react-phone-input-2/lib/style.css";
import Modalstyle from "./modal.module.scss";
import "../../assets/scss/common.scss";
import {
  AREA_CODES,
  AU_PHONE_REGEX,
  COUNTRY_CODE_AU_SMALL,
  COUNTRY_CODE_NZ,
  COUNTRY_CODE_NZ_SMALL,
  NZ_PHONE_REGEX,
  showToastType,
  WHITESPACE_REGEX
} from "../../utils/constants/appConstants";
import {
  buttonName,
  commonErrMsg,
  identityText,
  signup
} from "../../locales/en";
import {
  getFetchCountryCodeURL,
  getOnboardSendLinkUrl
} from "../../utils/constants/apiEndPoints";
import CommonModal from "./CommonModal";
import ErrorBoundary from "../error-boundary";
import ModalFallback from "../error-boundary/fallback/ModalFallback";

const OnboardingIdentityModal = ({ isOpen, toggle }) => {
  const initialRender = useRef(true);
  const [loading, setLoading] = useState(false);
  const [selectCountry, setSelectCountry] = useState(COUNTRY_CODE_AU_SMALL);
  const [country, setCountry] = useState(COUNTRY_CODE_AU_SMALL);
  const [validatePhnumber, setValidatePhnumber] = useState(false);
  const initialValues = {
    phoneNumber: "",
    error: ""
  };
  const [values, setValues] = useState(initialValues);
  const initialErrors = {
    phoneNumber: ""
  };
  const [errors, setErrors] = useState(initialErrors);

  const fetchUserLocation = useCallback(async () => {
    try {
      const response = await fetch("https://ipapi.co/json/");
      const data = await response.json();
      if (data.country_code === COUNTRY_CODE_NZ) {
        setCountry(COUNTRY_CODE_NZ_SMALL);
      } else {
        setCountry(COUNTRY_CODE_AU_SMALL);
      }
    } catch {
      // handle error catch
    }
  }, []);

  const fetchCountryCode = useCallback(
    async (phoneNumber, country) => {
      try {
        const response = await privateAxios.get(getFetchCountryCodeURL, {
          params: { number: phoneNumber, countryCode: country }
        });
        if (response.status === 200) {
          setValidatePhnumber(true);
        } else {
          setValidatePhnumber(false);
        }
      } catch {
        setValidatePhnumber(false);
      }
    },
    [privateAxios]
  );

  const selectedCountry = (phoneNumber) => {
    const countryCode = phoneNumber.slice(0, 2);
    let country = null;
    if (countryCode === AREA_CODES.au[0]) {
      country = COUNTRY_CODE_AU_SMALL;
    } else if (countryCode === AREA_CODES.nz[0]) {
      country = COUNTRY_CODE_NZ_SMALL;
    }
    return country;
  };

  const requestPhoneNumber = (phoneNumnber) => {
    const result = phoneNumnber.slice(2).replace(/[()-\s]/g, "");
    return result;
  };

  const handlePhInputChange = (field, rawValue, countryCode) => {
    // Drop the redundant trunk 0 as it is typed so it never consumes a mask slot,
    // which would otherwise push the genuinely typed last digit out.
    const value = stripTrunkZeroAfterCountryCode(rawValue);
    setValues({ ...values, [field]: value });
    setValidatePhnumber(false);

    if (selectCountry !== countryCode) {
      const result = validateField(field, value);
      if (result !== "") return;
      const phoneNumber = requestPhoneNumber(value);
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };

  const handleBlur = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value
    }));
    // Trigger validation for the specific field
    const result = validateField(field, value);
    if (result !== "") return;
    const phoneNumber = requestPhoneNumber(values.phoneNumber);
    if (phoneNumber && phoneNumber !== value) {
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "phoneNumber": {
        const trimmedValue = value.replace(WHITESPACE_REGEX, "");
        const numericValue = value.trim().replace(/[^\d]/g, "");

        if (
          AU_PHONE_REGEX.test(trimmedValue) ||
          NZ_PHONE_REGEX.test(trimmedValue)
        ) {
          error = signup?.errors.startnumber;
        } else if (
          trimmedValue === "" ||
          Object.values(AREA_CODES).flat().includes(trimmedValue)
        ) {
          error = signup?.errors?.numberRequired;
        } else if (!isValidPhoneLength(numericValue)) {
          error = signup?.errors?.numberRequired;
        }
        break;
      }

      default:
        break;
    }

    if (typeof error === "object" && !React.isValidElement(error)) {
      error = Object.values(error).flat().join(", ");
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error
    }));

    return error;
  };

  const handleCloseModal = () => {
    toggle();
    setErrors({
      phoneNumber: ""
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const phoneNumberError = validateField(
      signup.signupBox.phoneNumber,
      values.phoneNumber
    );

    const errors = {
      phoneNumber: phoneNumberError
    };

    setErrors(errors);
    // Check if any errors exist
    const hasErrors = Object.values(errors).some((error) => error.length > 0);
    if (hasErrors) {
      return;
    }

    // Convert the phone number to E.164 format
    const e164PhoneNumber = `+${values.phoneNumber.replace(/\D/g, "")}`;

    try {
      const payloadData = {
        phoneNumber: e164PhoneNumber
      };
      const response = await privateAxios.post(
        getOnboardSendLinkUrl,
        {},
        {
          params: payloadData
        }
      );
      if (response.status === 200) {
        showToast(response.data.message, showToastType.success);
        setLoading(true);
        document.body.classList.add("scroll_hide");
      }
      toggle();
      setValues({
        phoneNumber: ""
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.join(", ") ||
        error.response?.data?.message ||
        commonErrMsg?.item4;
      showToast(errorMessage, showToastType.error);
    }
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      fetchUserLocation();
    }
  }, [fetchUserLocation]);

  if (loading) {
    return (
      <div className="spinner-wrap bg-light loaderposition">
        <div className="snipper-wrap">
          <div className="spinner-border custom-spinner-color" />
          <h3>{identityText.onboardingIntgModal.thismayTake}</h3>
        </div>
      </div>
    );
  }

  const footerButtons = [
    {
      label: buttonName?.close,
      className: "btn btn-secondary",
      onClick: handleCloseModal
    },
    {
      label: buttonName?.sendLink,
      className: "btn btn-primary",
      onClick: handleSubmit
    }
  ];

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title={identityText?.sendLink}
      footerButtons={footerButtons}
    >
      <ErrorBoundary fallback={<ModalFallback />}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <div className={`${Modalstyle.modalInputwrap}`}>
              <p>{identityText.onboardingIntgModal.enterYourPh}</p>
            </div>
          </div>
          <div className="col-md-12">
            <div
              className={`${Modalstyle.modalInputwrap} ${Modalstyle.validatePhoneNumber}`}
            >
              <PhoneInput
                inputProps={{
                  name: "phone_number",
                  id: "phone_number"
                }}
                className="ph-with-country-code"
                country={country}
                value={values.phoneNumber}
                countryCodeEditable={false}
                onlyCountries={[COUNTRY_CODE_NZ_SMALL, COUNTRY_CODE_AU_SMALL]}
                areaCodes={{ au: AREA_CODES.au, nz: AREA_CODES.nz }}
                masks={{ au: "(..) .... ...", nz: "(..) .... ...." }}
                onChange={(value, event) => {
                  setSelectCountry(event.countryCode);
                  handlePhInputChange("phoneNumber", value, event.countryCode);
                }}
                onBlur={() => handleBlur("phoneNumber", values.phoneNumber)}
              />
              {validatePhnumber && (
                <div className="small primary-color">
                  <FaCheck /> {signup?.signupBox.phoneOkay}
                </div>
              )}
              {errors.phoneNumber && (
                <div className="text-danger small">{errors.phoneNumber}</div>
              )}
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </CommonModal>
  );
};

OnboardingIdentityModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired
};
export default OnboardingIdentityModal;
